<script setup lang="ts">
import { OctagonXIcon, XIcon } from 'lucide-vue-next'
import { ref, onMounted, onUnmounted } from 'vue'

interface AdBlockDetectorEmits {
  'adblock-detected': []
  'message-closed': []
  'subscription-clicked': []
}

const emit = defineEmits<AdBlockDetectorEmits>()

const showAdBlockMessage = ref<boolean>(false)
const adBlockDetected = ref<boolean>(false)
let checkInterval: NodeJS.Timeout | null = null

const { t } = useI18n()

const detectAdBlock = (): void => {
  checkAdBlockByFetch()
}

const checkAdBlockByFetch = (): void => {
  if (typeof fetch !== 'undefined') {
    fetch('https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js', {
      method: 'HEAD',
      mode: 'no-cors'
    }).catch(() => {
      handleAdBlockDetected()
    })
  }
}

const handleAdBlockDetected = (): void => {
  if (!adBlockDetected.value) {
    adBlockDetected.value = true

    setTimeout(() => {
      showAdBlockMessage.value = true
      emit('adblock-detected')
    }, 3000)
  }
}

const closeMessage = (): void => {
  showAdBlockMessage.value = false
  emit('message-closed')
}

const whitelistSite = (): void => {
  alert(t('feature.adb_detector.whitelist_instructions'))
  closeMessage()
}

onMounted(() => {
  detectAdBlock()
  checkInterval = setInterval(detectAdBlock, 30000)
})

onUnmounted(() => {
  if (checkInterval) {
    clearInterval(checkInterval)
  }
})
</script>

<template>
  <div v-if="showAdBlockMessage" class="fixed inset-0 z-50 font-sans">
    <div class="absolute inset-0 bg-black/50 backdrop-blur-sm" @click="closeMessage" />
    <div
      class="absolute left-1/2 top-1/2 w-[90%] max-w-lg -translate-x-1/2 -translate-y-1/2 transform rounded-xl bg-white shadow-2xl duration-300 animate-in fade-in zoom-in-95"
    >
      <div class="flex items-center rounded-t-xl border-b border-gray-200 bg-gray-50 px-6 py-4">
        <div class="mr-3 flex-shrink-0 text-red-500">
          <OctagonXIcon class="size-7" />
        </div>
        <Heading tag="h5" class="flex-1">{{ $t('feature.adb_detector.heading') }}</Heading>
        <Button variant="ghost" size="icon" @click="closeMessage">
          <XIcon />
        </Button>
      </div>

      <div class="px-6 py-4">
        <p class="mb-4 text-sm leading-relaxed">
          {{ $t('feature.adb_detector.text1') }}
        </p>
        <p class="text-sm leading-relaxed">{{ $t('feature.adb_detector.text2') }}</p>
      </div>

      <div
        class="flex flex-col gap-3 rounded-b-xl border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row"
      >
        <Button variant="blue" @click="whitelistSite">
          {{ $t('feature.adb_detector.add_to_exceptions_button') }}
        </Button>
        <Button variant="gray" @click="closeMessage">
          {{ $t('feature.adb_detector.continue_button') }}
        </Button>
      </div>
    </div>
  </div>
</template>
